import React, { useState } from "react";
import { Button, ButtonBase, Typography, makeStyles } from "@material-ui/core";
import {
  DescriptionOutlined,
  GetApp,
  InsertDriveFileOutlined,
  OpenInNew,
  PictureAsPdfOutlined,
} from "@material-ui/icons";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: {
    width: "100%",
    maxWidth: 320,
  },

  card: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    padding: 14,
    borderRadius: 14,
    backgroundColor: theme.mode === "light" ? "#f8f9fb" : "#1f2c33",
    border: `1px solid ${theme.mode === "light" ? "#dde3ea" : "#23323a"}`,
  },

  row: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },

  thumb: {
    width: 72,
    minWidth: 72,
    height: 88,
    borderRadius: 14,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    position: "relative",
    overflow: "hidden",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
  },

  thumbIcon: {
    fontSize: 34,
  },

  extBadge: {
    position: "absolute",
    bottom: 8,
    left: "50%",
    transform: "translateX(-50%)",
    minWidth: 42,
    padding: "3px 8px",
    borderRadius: 999,
    backgroundColor: "rgba(17, 24, 39, 0.32)",
    textAlign: "center",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 0.8,
    textTransform: "uppercase",
  },

  content: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },

  label: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: theme.mode === "light" ? "#64748b" : "#8ea3b6",
  },

  fileName: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.mode === "light" ? "#122433" : "#f2f7fb",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
  },

  meta: {
    fontSize: 12,
    color: theme.mode === "light" ? "#506070" : "#c5d4df",
  },

  actions: {
    display: "flex",
    gap: 8,
  },

  actionButton: {
    flex: 1,
  },

  compactLink: {
    width: "100%",
    maxWidth: 280,
  },

  compactCard: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 10,
    borderRadius: 12,
    textAlign: "left",
    backgroundColor: theme.mode === "light" ? "#f8f9fb" : "#1f2c33",
    border: `1px solid ${theme.mode === "light" ? "#dde3ea" : "#23323a"}`,
  },

  compactThumb: {
    width: 46,
    minWidth: 46,
    height: 56,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    color: "#fff",
    overflow: "hidden",
  },

  compactThumbIcon: {
    fontSize: 24,
  },

  compactExtBadge: {
    position: "absolute",
    bottom: 4,
    left: "50%",
    transform: "translateX(-50%)",
    minWidth: 28,
    padding: "2px 5px",
    borderRadius: 999,
    backgroundColor: "rgba(17, 24, 39, 0.32)",
    textAlign: "center",
    fontSize: 8,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },

  compactContent: {
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },

  compactName: {
    fontSize: 13,
    fontWeight: 700,
    color: theme.mode === "light" ? "#122433" : "#f2f7fb",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
  },

  compactMeta: {
    fontSize: 11,
    color: theme.mode === "light" ? "#506070" : "#c5d4df",
  },
}));

const extractFileExtension = fileName => {
  if (!fileName || !fileName.includes(".")) {
    return "file";
  }

  return fileName.split(".").pop().toLowerCase();
};

const getFileVisual = extension => {
  if (extension === "pdf") {
    return {
      icon: PictureAsPdfOutlined,
      label: "PDF",
      background: "linear-gradient(180deg, #ef4444, #b91c1c)",
    };
  }

  if (["doc", "docx", "txt", "rtf", "odt"].includes(extension)) {
    return {
      icon: DescriptionOutlined,
      label: "Documento",
      background: "linear-gradient(180deg, #3b82f6, #1d4ed8)",
    };
  }

  if (["xls", "xlsx", "csv"].includes(extension)) {
    return {
      icon: InsertDriveFileOutlined,
      label: "Planilha",
      background: "linear-gradient(180deg, #22c55e, #15803d)",
    };
  }

  if (["zip", "rar", "7z"].includes(extension)) {
    return {
      icon: InsertDriveFileOutlined,
      label: "Compactado",
      background: "linear-gradient(180deg, #f59e0b, #b45309)",
    };
  }

  return {
    icon: InsertDriveFileOutlined,
    label: "Arquivo",
    background: "linear-gradient(180deg, #64748b, #334155)",
  };
};

const MessageDocumentPreview = ({
  fileName,
  url,
  compact = false,
}) => {
  const classes = useStyles();
  const [downloading, setDownloading] = useState(false);
  const safeFileName = fileName || "Arquivo";
  const extension = extractFileExtension(safeFileName);
  const { icon: IconComponent, label, background } = getFileVisual(extension);

  const handleDownload = async event => {
    event.preventDefault();
    event.stopPropagation();

    if (!url || downloading) {
      return;
    }

    try {
      setDownloading(true);

      const { data } = await api.get(url, {
        responseType: "blob",
      });

      const objectUrl = window.URL.createObjectURL(data);
      const anchor = document.createElement("a");

      anchor.href = objectUrl;
      anchor.download = safeFileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      toastError(error);
    } finally {
      setDownloading(false);
    }
  };

  if (compact) {
    return (
      <ButtonBase
        className={classes.compactLink}
        component="a"
        href={url || "#"}
        target="_blank"
        rel="noopener noreferrer"
        disabled={!url}
      >
        <div className={classes.compactCard}>
          <div className={classes.compactThumb} style={{ background }}>
            <IconComponent className={classes.compactThumbIcon} />
            <span className={classes.compactExtBadge}>{extension}</span>
          </div>

          <div className={classes.compactContent}>
            <Typography className={classes.compactName}>{safeFileName}</Typography>
            <Typography className={classes.compactMeta}>{label}</Typography>
          </div>
        </div>
      </ButtonBase>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.card}>
        <div className={classes.row}>
          <div className={classes.thumb} style={{ background }}>
            <IconComponent className={classes.thumbIcon} />
            <span className={classes.extBadge}>{extension}</span>
          </div>

          <div className={classes.content}>
            <Typography className={classes.label}>{label}</Typography>
            <Typography className={classes.fileName}>{safeFileName}</Typography>
            <Typography className={classes.meta}>
              Toque para abrir ou baixar
            </Typography>
          </div>
        </div>

        <div className={classes.actions}>
          <Button
            className={classes.actionButton}
            variant="outlined"
            color="primary"
            startIcon={<OpenInNew />}
            target="_blank"
            href={url}
          >
            Abrir
          </Button>

          <Button
            className={classes.actionButton}
            variant="outlined"
            startIcon={<GetApp />}
            onClick={handleDownload}
            disabled={!url || downloading}
          >
            {downloading ? "Baixando..." : "Baixar"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MessageDocumentPreview;
